import { Link } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { pickLang } from "@/data/stoicosCards";
import { AL_LANDING, SVF_PAGE } from "@/data/ancientLibrary";
import { AboutEditionNote, AlBreadcrumb, useAlLabels } from "@/pages/ancient/common";

export default function AncientLibraryPage() {
  const { lang, labels } = useAlLabels();

  useSEO({
    title: "Ancient Library — Early Stoic Sources in Greek & Latin | StoicOs.ai",
    description: "The StoicOs Ancient Library: a multilingual digital source library for early Stoicism. Surviving Greek and Latin fragments and testimonia, translated and interpreted with scholarly care.",
    canonical: "https://stoicos.ai/ancient-library",
    jsonLd: [
      webPage({ name: "Ancient Library", description: "Multilingual digital source library for early Stoicism.", url: "/ancient-library" }),
      breadcrumb([{ name: "Home", url: "/" }, { name: "Ancient Library", url: "/ancient-library" }]),
    ],
  });

  return (
    <main data-testid="ancient-library-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <AlBreadcrumb items={[{ to: "/", label: "Home" }, { label: labels.library }]} />
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">StoicOs</p>
        <h1 data-testid="al-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone tracking-tight leading-[1.05]">Ancient Library</h1>
        <p className="mt-6 font-serif italic text-xl md:text-2xl text-gold/90">{pickLang(AL_LANDING.lede, lang)}</p>
        <p className="mt-8 text-bone/90 font-reading text-lg md:text-xl leading-[1.85]">{pickLang(AL_LANDING.intro, lang)}</p>

        {/* Guiding structure */}
        <div className="mt-12 flex flex-wrap items-center gap-y-3" data-testid="al-principle">
          {AL_LANDING.principle.map((step, i) => (
            <span key={i} className="inline-flex items-center">
              {i > 0 && <span className="mx-3 text-gold/60">→</span>}
              <span className="px-3 py-1.5 border border-gold/25 text-[11px] tracking-[0.18em] uppercase text-gold/90">{pickLang(step, lang)}</span>
            </span>
          ))}
        </div>

        <p className="mt-10 text-bone/80 font-reading text-base md:text-lg leading-[1.85]">{pickLang(AL_LANDING.integrity, lang)}</p>

        {/* Collections */}
        <section className="mt-16">
          <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{labels.collections}</h2>
          <Link to="/ancient-library/svf" data-testid="al-collection-svf" className="group block p-8 md:p-10 border border-gold/25 bg-ink-elevated/40 hover:border-gold/50 transition-colors">
            <p className="text-[10px] tracking-[0.28em] uppercase text-gold/80">{labels.available}</p>
            <h3 className="mt-3 font-serif text-2xl md:text-3xl text-bone tracking-tight group-hover:text-gold transition-colors">{SVF_PAGE.title}</h3>
            <p className="mt-3 text-bone/75 font-reading text-base leading-[1.8]">{pickLang(SVF_PAGE.subtitle, lang)}</p>
          </Link>
          <div className="mt-8">
            <h3 className="text-[11px] tracking-[0.25em] uppercase text-fog/70 mb-4">{labels.futureWitnesses}</h3>
            <div className="flex flex-wrap gap-2.5">
              {AL_LANDING.futureWitnesses.map((w) => (
                <span key={w} className="px-3 py-1.5 border border-white/10 text-[11px] tracking-[0.18em] uppercase text-fog/60">{w} · {labels.inPreparation}</span>
              ))}
            </div>
          </div>
        </section>

        <AboutEditionNote />
      </div>
    </main>
  );
}
